import { memo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router'
import dayjs from 'dayjs'
import { useHover } from 'usehooks-ts'

import DeleteOutlinedIcon from '@ant-design/icons/DeleteOutlined'
import DoneAllIcon from '@icons/DoneAll'
import ReplyIcon from '@icons/ReplyIcon'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import Divider from '@mui/material/Divider'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@components/_mui/IconButton'
import Link from '@components/_mui/Link'
import Typography from '@components/_mui/Typography'

import useDialog from '@shared/hooks/src/useDialog'
import { useDeleteMessage } from '@shared/messaging/src/hooks'
import { useMe } from '@shared/providers/src/MeProvider'
import { convertPossibleRichTextToPlainText, handleError, UserRole } from '@shared/utils'

import Avatar from '@components/Avatar'
import ConfirmationV2 from '@components/Dialog/ConfirmationV2'
import RichText from '@components/RichText'

import { useHighlight } from './Message.hooks'
import { styles } from './Message.utils'

function Message({ threadId, data, isOwn, mini, readOnly = false, highlighted = false, onReply, onReact, isRead = false }) {
  const me = useMe()
  const ref = useRef(null)
  const isHovered = useHover(ref)
  const theme = useTheme()
  const isHighlighted = useHighlight(highlighted)

  const deleteMessage = useDeleteMessage(threadId)

  const position = isOwn ? 'right' : 'left'
  const canDelete = me.superAdmin

  const handleDelete = useDialog({
    component: ConfirmationV2,
    props: ({ item: id, close }) => ({
      variant: 'error',
      title: 'Delete Message',
      description: 'Are you sure you want to delete this message? This action cannot be undone.',
      rejectLabel: 'Cancel',
      confirmLabel: 'Delete',
      onReject: () => close(),
      onConfirm: () => {
        return deleteMessage
          .mutateAsync(id)
          .then(() => close())
          .catch(handleError)
      },
    }),
  })

  return (
    <Box id={data.id} sx={styles.container(position)}>
      <Stack ref={ref} direction={position === 'right' ? 'row-reverse' : 'row'} spacing={1} sx={styles.content(position)}>
        {!isOwn && <MessageAuthor hover="card" user={data.user} />}
        <Stack sx={{ flex: '1 1 auto', gap: 0.5 }}>
          <Card sx={styles.card(position, isHighlighted)}>
            <Stack sx={{ gap: 0.5 }}>
              {(!isOwn || !mini) && (
                <>
                  {data.user.role === UserRole.Patient ? (
                    <div>
                      <Link component={RouterLink} to={`/app/patients/${data.user.id}`} color="inherit" variant="body2" target="_blank">
                        {data.user.fullName}
                      </Link>
                    </div>
                  ) : (
                    <Typography color="inherit" variant="body2">
                      {data.user.fullName}
                    </Typography>
                  )}
                </>
              )}

              {(data.relatedMessageId || data.relatedMessage || data.relatedMessageError) && (
                <RelatedMessage data={data.relatedMessage} position={position} error={data.relatedMessageError} />
              )}
              <RichText content={data.message} />
            </Stack>
          </Card>
          <Box sx={styles.date(position)}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
                {dayjs(data.created).format('L LT')}
                {data.sentOnBehalf && ` by ${data.sentBy.fullName}`}
              </Typography>
              {isOwn && isRead && (
                <Tooltip title="Patient has read this message" placement="top" arrow>
                  <DoneAllIcon style={{ fontSize: 18, color: theme.palette.success.main }} />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>
        <MessageActions
          isVisible={!readOnly && isHovered}
          isOwn={isOwn}
          onReact={onReact}
          onReply={onReply}
          canDelete={canDelete}
          onDelete={() => handleDelete(data.id)}
        />
      </Stack>
    </Box>
  )
}

// TODO Can be improved by scrolling into message instead of expanding it in place
const RelatedMessage = ({ data, error, position }) => {
  const [expanded, setExpanded] = useState(false)

  const showLoading = !data && !error
  const showData = Boolean(data)
  const showError = Boolean(error)

  return (
    <CardActionArea sx={styles.relatedMessage(position)} disabled={!showData || showError} onClick={() => setExpanded((v) => !v)}>
      <Box sx={{ display: 'inline-grid' }}>
        {showLoading && (
          <>
            <Typography variant="body2">
              <Skeleton width={150} />
            </Typography>
            <Typography variant="body2">
              <Skeleton width={300} />
            </Typography>
          </>
        )}

        {showError && (
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" sx={{ color: 'error.main', fontStyle: 'italic' }}>
              Failed to load related message
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              This message may have been deleted or is no longer available
            </Typography>
          </Box>
        )}

        {showData && (
          <>
            <Typography variant="body2">{data.user.fullName}</Typography>
            {expanded ? (
              <RichText content={data.message} variant="body2" />
            ) : (
              <Typography variant="body2" noWrap>
                {convertPossibleRichTextToPlainText(data.message)}
              </Typography>
            )}
          </>
        )}
      </Box>
    </CardActionArea>
  )
}

const MessageActions = ({ isVisible, isOwn, onReact, onReply, canDelete, onDelete }) => {
  // Don't show anything if not visible or no actions available
  if (!isVisible || (!onReact && !onReply && !canDelete)) return null

  const showReactions = !isOwn && onReact
  const showReply = !isOwn && onReply
  const showDelete = canDelete

  // Helper to determine if we need a divider between sections
  const needsDivider = (hasLeft, hasRight) => hasLeft && hasRight

  return (
    <Grow in={isVisible} unmountOnExit>
      <Paper elevation={4} sx={styles.popover}>
        <Stack direction="row" spacing={1}>
          {showReactions && (
            <Stack direction="row" spacing={1}>
              <ReactButton value="👍" onClick={onReact} />
              <ReactButton value="👎" onClick={onReact} />
            </Stack>
          )}

          {needsDivider(showReactions, showDelete || showReply) && <Divider orientation="vertical" sx={{ height: 30 }} />}

          {showDelete && (
            <Tooltip title="Delete message" placement="top" arrow>
              <IconButton size="small" onClick={onDelete} color="error">
                <DeleteOutlinedIcon style={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {needsDivider(showDelete, showReply) && <Divider orientation="vertical" sx={{ height: 30 }} />}

          {showReply && (
            <IconButton size="small" onClick={onReply} color="secondary">
              <ReplyIcon style={{ fontSize: 24 }} />
            </IconButton>
          )}
        </Stack>
      </Paper>
    </Grow>
  )
}

const ReactButton = ({ value, onClick }) => (
  <IconButton size="small" onClick={() => onClick(value)} color="secondary" sx={{ fontSize: 18 }}>
    {value}
  </IconButton>
)

export const MessageAuthor = ({ user, hover }) => <Avatar hover={hover} user={user} variant="circular" />

export const MessageLoading = ({ isOwn }) => {
  const position = isOwn ? 'right' : 'left'

  return (
    <Box sx={styles.container(position)}>
      <Stack direction={position === 'right' ? 'row-reverse' : 'row'} spacing={2} sx={styles.content(position)}>
        {!isOwn && <Skeleton variant="circular" width={40} height={40} />}
        <Stack sx={{ flex: '1 1 auto', gap: 0.5 }}>
          <Card sx={styles.card(position)}>
            <Stack sx={{ gap: 0.5 }}>
              <Typography variant="body2">
                <Skeleton width={100} />
              </Typography>
              <Typography>
                <Skeleton width={250} />
              </Typography>
            </Stack>
          </Card>
          <Box sx={styles.date(position)}>
            <Typography variant="caption">
              <Skeleton width={60} />
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}

export default memo(Message)
